/*
 * Exercise B content picker (FR-EXB-02).
 *
 * Responsibilities (this module only):
 *   - Translate a caller-supplied level into the spec's "target
 *     difficulty" content type (the difficulty ladder: L1–2 word,
 *     L3–4 word-combo, L5–6 short_line, L7–8 long_line).
 *   - Pick one content entry from a caller-supplied corpus suitable
 *     for an Exercise B question at that level.
 *   - For L3–4 current-level questions, construct a synthetic
 *     "combo" entry by concatenating two `type=word` rows at runtime.
 *     This is Q-04's default resolution — no CSV schema change.
 *   - Walk a deterministic fallback chain when the target type has no
 *     eligible entries (long_line → short_line → word → null), and
 *     emit ONE warning per fallback step.
 *   - Honor the FR-LOGIC-01 review mix: when `isReview` is true, any
 *     difficulty up to and including the target is eligible (uniform
 *     pick across all of them, no combo construction).
 *
 * Scope boundary: this picker chooses *content*. It does not choose
 * which character position to blank out (that's FR-EXB-01 / #19) and
 * does not pick distractor characters (also FR-EXB-01). The downstream
 * consumer wraps the returned entry in a question shape.
 *
 * Pure function: no module-level state, no `getCorpus()` calls. The
 * corpus, level, and review flag all come from the caller. RNG and
 * warning sink are injectable so the tests stay deterministic and
 * silent under vitest.
 */

import type { RuneEntry, RuneEntryType } from './csv';

/**
 * Difficulty rank: smaller = easier. Used to order the fallback chain
 * and to compute the "any difficulty up to and including the target"
 * eligibility set for review questions.
 */
const TYPE_RANK: Record<RuneEntryType, number> = {
  word: 0,
  short_line: 1,
  long_line: 2,
};

/**
 * Fallback chain when the target type yields zero candidates. Keyed by
 * target type; each value is the strict-easier type to try next.
 * `word` has no easier type — a `word` miss is unrecoverable and the
 * picker returns null.
 */
const FALLBACK_NEXT: Record<RuneEntryType, RuneEntryType | null> = {
  long_line: 'short_line',
  short_line: 'word',
  word: null,
};

/**
 * Map a 1-based level to the spec's target content type.
 *
 * Throws RangeError on out-of-range levels — callers should have
 * already validated `level` against the 1–8 unlock range, so reaching
 * this with anything else is a programmer error worth surfacing
 * loudly rather than silently degrading to a default.
 */
export function targetTypeForLevel(level: number): RuneEntryType {
  if (!Number.isInteger(level) || level < 1 || level > 8) {
    throw new RangeError(`targetTypeForLevel: level must be an integer in [1, 8] (got ${level})`);
  }
  if (level <= 2) return 'word';
  if (level <= 4) return 'word'; // combo is built at runtime; source type stays 'word'
  if (level <= 6) return 'short_line';
  return 'long_line';
}

export interface PickExerciseBContentOptions {
  /**
   * When true, the question is part of the 25% review mix
   * (FR-LOGIC-01) — any difficulty at or below the target type is
   * eligible, and no combo construction is performed (review keeps
   * source entries intact). When false (default), the current-level
   * rule applies: strict target type + fallback chain, plus L3–4
   * combo construction.
   */
  isReview?: boolean;
  /**
   * Injectable RNG returning a float in [0, 1). Default Math.random.
   * Threaded through every random choice in this module so a single
   * seed drives the whole pick (selection + combo pairing).
   */
  random?: () => number;
  /**
   * Sink for fallback warnings. Default console.warn. Tests inject a
   * spy and assert on call count + message shape.
   */
  logger?: (message: string) => void;
}

/**
 * Pick ONE content entry suitable for an Exercise B question at the
 * caller-supplied level.
 *
 * Behavior summary:
 *   - `isReview=false` (current-level question):
 *       - Filter to entries with `level <= level` AND `type === target`.
 *       - L3–4 + target='word': pick two distinct words and return a
 *         synthetic combo entry (latin/rune/english concatenated).
 *         If only 1 word is available, return that single entry and
 *         warn.
 *       - L1–2 / L5–6 / L7–8: pick one uniformly at random.
 *       - On empty pool, walk the fallback chain (long_line →
 *         short_line → word), emitting one warning per step.
 *   - `isReview=true`:
 *       - Filter to entries with `level <= level` AND
 *         `TYPE_RANK[type] <= TYPE_RANK[target]`.
 *       - Pick one uniformly at random. No combo. No fallback warning
 *         (review naturally spans all easier types — there's nothing
 *         to fall back to that wasn't already eligible).
 *
 * Returns null only when no eligible entry exists at any difficulty.
 * With a non-empty corpus and a level that has unlocked at least one
 * `word`, this is unreachable in practice.
 */
export function pickExerciseBContent(
  corpus: readonly RuneEntry[],
  level: number,
  options?: PickExerciseBContentOptions,
): RuneEntry | null {
  const targetType = targetTypeForLevel(level);
  const isReview = options?.isReview ?? false;
  const random = options?.random ?? Math.random;
  const logger = options?.logger ?? ((msg: string) => console.warn(msg));

  if (isReview) {
    return pickReviewEntry(corpus, level, targetType, random);
  }

  return pickCurrentLevelEntry(corpus, level, targetType, random, logger);
}

/**
 * Review path: any difficulty up to the target type is eligible.
 * Uniform random pick across the combined pool. No combo, no warnings.
 * Returns null only if the combined pool is empty (corpus has no
 * entry whose level ≤ `level` of any allowed type).
 */
function pickReviewEntry(
  corpus: readonly RuneEntry[],
  level: number,
  targetType: RuneEntryType,
  random: () => number,
): RuneEntry | null {
  const targetRank = TYPE_RANK[targetType];
  const pool = corpus.filter(
    (entry) => entry.level <= level && TYPE_RANK[entry.type] <= targetRank,
  );
  return pickOne(pool, random);
}

/**
 * Current-level path: strict target type + L3–4 combo + fallback
 * chain. Emits exactly one warning per fallback step (and one extra
 * warning for the "only 1 word, no combo possible" degraded path at
 * L3–4).
 */
function pickCurrentLevelEntry(
  corpus: readonly RuneEntry[],
  level: number,
  targetType: RuneEntryType,
  random: () => number,
  logger: (message: string) => void,
): RuneEntry | null {
  let currentType: RuneEntryType | null = targetType;

  // Walk the fallback chain. Each iteration tries one difficulty type;
  // on empty pool we descend and warn, until we either find candidates
  // or exhaust the chain (word → null).
  while (currentType !== null) {
    const pool = corpus.filter((entry) => entry.level <= level && entry.type === currentType);

    if (pool.length > 0) {
      // L3–4 combo rule applies only at the original target type — once
      // we've fallen back to an easier type, combo construction would
      // be silly (review is the path for "anything easier"). Even at
      // the target, combo only triggers when target IS 'word' (true at
      // L3–4 by the ladder).
      const isComboLevel = (level === 3 || level === 4) && currentType === 'word';
      if (isComboLevel) {
        return buildCombo(pool, level, random, logger);
      }
      return pickOne(pool, random);
    }

    // Empty pool at this difficulty — descend and warn ONCE.
    const next: RuneEntryType | null = FALLBACK_NEXT[currentType];
    if (next === null) {
      // We're already at the easiest type and still empty: unrecoverable.
      // No final warning here — the caller observing `null` plus the
      // chain of warnings already tells the whole story.
      return null;
    }
    logger(`[exercise-b] no ${currentType} entries at level <=${level}; falling back to ${next}`);
    currentType = next;
  }

  return null;
}

/**
 * Build a synthetic two-word combo entry for L3–4 current-level
 * questions. Picks two DISTINCT words from `pool` (same-entry-twice
 * would render as "X X" which is not what Q-04 means by "combo").
 *
 * Degradation: if `pool` has only 1 entry, returns that single entry
 * and emits a warning. The caller can't build a combo from one word,
 * and falling back to short_line/long_line would skip past content
 * that IS at the right difficulty — better to ship the lone word.
 */
function buildCombo(
  pool: readonly RuneEntry[],
  level: number,
  random: () => number,
  logger: (message: string) => void,
): RuneEntry {
  if (pool.length < 2) {
    logger(
      `[exercise-b] insufficient word entries for combo at level <=${level}; falling back to single word`,
    );
    // Safe: pickCurrentLevelEntry only calls buildCombo when pool.length >= 1.
    return pool[0]!;
  }

  // Two distinct picks. First pick by index; second pick from the
  // remaining (length-1) candidates and shift past the first if we
  // landed at or after it. This keeps the second pick uniform over
  // the surviving entries without an O(n) array copy.
  const firstIdx = Math.floor(random() * pool.length);
  const first = pool[firstIdx]!;

  const secondPickFromRemaining = Math.floor(random() * (pool.length - 1));
  const secondIdx =
    secondPickFromRemaining >= firstIdx ? secondPickFromRemaining + 1 : secondPickFromRemaining;
  const second = pool[secondIdx]!;

  return {
    latin: `${first.latin} ${second.latin}`,
    // No separator between glyph runs — the custom font renders them
    // as a continuous string; a space would inject a wordbreak.
    rune: `${first.rune}${second.rune}`,
    english: `${first.english} ${second.english}`,
    // "As hard as its hardest source" — keeps the level field
    // meaningful if a downstream consumer ever filters combos by level.
    level: Math.max(first.level, second.level),
    type: 'word',
  };
}

/**
 * Uniform random pick from `pool`, or null when `pool` is empty.
 * Centralized so `random()` is called exactly once per pick and the
 * empty-pool sentinel is consistent.
 */
function pickOne<T>(pool: readonly T[], random: () => number): T | null {
  if (pool.length === 0) return null;
  const idx = Math.floor(random() * pool.length);
  return pool[idx]!;
}
